import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {SelectElem} from '../../../components/forms/inputs'
import {taskTypes} from '../../../components/filters/constants'
import {
  TaskType,
  TYPE_COLUMN,
  QUESTION_COLUMN,
  PARAPHRASE,
  columnTypes,
  paraphraseable,
  ColumnType,
  ColumnConfig,
  ColumnsConfig,
} from '../../../services/types'
import type {EvaluationStageProps} from './types'

type ColumnProps = {
  column: string
  config: ColumnConfig
  columns: ColumnsConfig
  onChange: (columns: ColumnsConfig) => void
}
const Column = ({column, config, columns, onChange}: ColumnProps) => {
  const changeType = (val: ColumnType) => {
    if ([TYPE_COLUMN, QUESTION_COLUMN].includes(val)) {
      Object.values(columns)
        .filter(({columnType}) => columnType === val)
        .forEach((conf) => {
          conf.columnType = undefined
        })
    }
    onChange({...columns, [column]: {...config, columnType: val}})
  }
  const setParaphrase = (val: string) => {
    const {paraphrases} = columns[val]
    columns[val].paraphrases = [...(paraphrases || []), column]
    columns[column].paraphraseOf = val
    onChange({...columns})
  }

  return (
    <Stack
      direction={{sm: 'column', md: 'row'}}
      justifyContent="flex-start"
      alignItems="center"
      spacing={4}
    >
      <Typography variant="h5" sx={{width: '35%', minWidth: '300px'}}>
        {column}
      </Typography>
      <SelectElem
        label="Column type"
        values={columnTypes}
        value={config.columnType || ''}
        onChange={changeType}
        unselected="Ignored"
        sx={{minWidth: '320px'}}
      />
      {config.columnType !== PARAPHRASE ? null : (
        <SelectElem
          label="Paraphrase of"
          values={Object.entries(columns).reduce(
            (acc, [col, {columnType}]) =>
              paraphraseable.includes(columnType || '') ? {...acc, [col]: col} : acc,
            {}
          )}
          value={config.paraphraseOf || ''}
          onChange={setParaphrase}
        />
      )}
    </Stack>
  )
}

const ConfigureColumns = ({evaluation, onChange}: EvaluationStageProps) => {
  const columns = evaluation.columns_mapping

  const updateConfig = (config: ColumnsConfig) => {
    onChange('columns_mapping', config)
  }

  return (
    <Stack spacing={2}>
      <Typography>
        Here you can configure how the columns from your CSV file should be used to construct tasks.
        Each row will result in a single task. You can configure the type of task on a per row
        level, by specifying a task type column. For rows where the task type is empty, or if no
        task type is selected, the default task type will be used.
      </Typography>
      <Typography>
        Different task types will require different columns, but every row <em>must</em> have a task
        text (i.e. question), which the models will respond to, so make sure there is a column with
        that data. You also need to specify a way to check whether a response was correct, but this
        will be task type dependant.
      </Typography>
      <Typography>
        All text columns (e.g. task text or correct answer) can have paraphrases. Paraphrases will
        always be used when provided, so if a column has paraphrases defined for it, the actual text
        of that column will never be sent to models - only you will be able to view it.
      </Typography>
      <SelectElem
        sx={{width: 300}}
        label="Default task type"
        values={taskTypes}
        alwaysSelected
        defaultValue="MCQ"
        onChange={(val: TaskType) => onChange('default_task_type', val)}
      />

      {columns &&
        Object.entries(columns).map(([col, config]) => (
          <Column
            key={col}
            column={col}
            config={config}
            columns={columns}
            onChange={updateConfig}
          />
        ))}
    </Stack>
  )
}

export default ConfigureColumns
